
import { useState, useEffect } from 'react';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { getDisplayName, getFormalName, normalizeTeam } from '@/lib/teams';

interface TeamForm {
  team_name: string;
  form_string: string;
  last_updated: string;
}

export const useTeamForm = (competitionId?: number) => {
  const [teamForms, setTeamForms] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (competitionId == null) return;
    fetchTeamForms();
  }, [competitionId]);

  const fetchTeamForms = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      const body: Record<string, unknown> = {};
      if (forceRefresh) body.forceRefresh = true;
      if (competitionId != null) body.competitionId = competitionId;

      const { data, error } = await invokeEdgeFunction('fetch-team-form', {
        body: Object.keys(body).length > 0 ? body : undefined,
      });

      if (error) {
        console.error('Error fetching team forms:', error);
        setError('Failed to fetch team forms');
        return;
      }

      if (data?.teamForms) {
        const formsMap: { [key: string]: string } = {};
        data.teamForms.forEach((form: TeamForm) => {
          // Store by normalized key for consistent lookups
          const normalizedKey = normalizeTeam(form.team_name);
          formsMap[normalizedKey] = form.form_string;
          
          // Also store by exact name, informal, and formal for backwards compatibility
          formsMap[form.team_name] = form.form_string;
          formsMap[getDisplayName(form.team_name)] = form.form_string;
          formsMap[getFormalName(form.team_name)] = form.form_string;
        });
        console.log('Team forms loaded:', Object.keys(formsMap).length, 'entries');
        setTeamForms(formsMap);
      }
    } catch (err) {
      console.error('Error fetching team forms:', err);
      setError('Failed to fetch team forms');
    } finally {
      setIsLoading(false);
    }
  };

  const getFormIcons = (teamName: string) => {
    // Try normalized key first, then exact match
    const form = teamForms[normalizeTeam(teamName)] || teamForms[teamName] || '';
    return form.split('').map(result => {
      switch (result) {
        case 'W': return '🟢';
        case 'L': return '🔴';
        case 'D': return '🟡';
        default: return '⚪';
      }
    });
  };

  return {
    teamForms,
    isLoading,
    error,
    getFormIcons,
    refetch: fetchTeamForms,
    forceRefresh: () => fetchTeamForms(true)
  };
};
